import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { Input, Toggle } from '@/components/ui'
import useFormConfirmation from '@/hooks/useFormConfirmation'
import { updateBien, fetchBien } from '../biens/biensSlice'
import { getFormField } from '../biens/SectionForm'
import EntretienForm from '../biens/entretiens/Form'
import BienContext from '../biens/BienContext'
import { completeRenewTask } from './renewTasksSlice'
import { RENEW_TASK_FORM_FIELDS } from './renewTaskTypes'

/**
 * Composant générique pour les formulaires de tâches de renouvellement
 *
 * @param {Object} props - Propriétés du composant
 * @param {Object} props.task - Tâche de renouvellement
 * @param {Function} props.onClose - Fonction appelée lors de la fermeture du formulaire
 * @param {Function} props.onSuccess - Fonction appelée après une soumission réussie
 * @returns {JSX.Element} - Élément JSX représentant le formulaire
 */
const RenewTaskForm = ({ task, onClose = () => {}, onSuccess = () => {} }) => {
  const dispatch = useDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [localBien, setLocalBien] = useState(null)
  
  // Récupérer les erreurs depuis le state Redux
  const errors = useSelector(state => state.biens.requestStatus.errors)
  const isUpdating = useSelector(state => state.biens.loadingStatus.update)
  
  // Déterminer les champs à afficher en fonction du type de tâche
  const formFields = RENEW_TASK_FORM_FIELDS[task.task_type] || []
  
  // Initialiser le formulaire avec les valeurs par défaut
  const { handleSubmit, control, reset, getValues, setValue, watch, formState: { errors: formErrors } } = useForm({
    defaultValues: {},
    mode: 'onChange',
    reValidateMode: 'onChange'
  })
  
  // Charger les données du bien lié à la tâche
  useEffect(() => {
    const fetchBienData = async () => {
      try {
        if (task.renewable && task.renewable.type === "Bien" && task.renewable.id) {
          // Récupérer le bien directement depuis l'API sans passer par le state global
          const response = await dispatch(fetchBien(task.renewable.id)).unwrap()
          // Stocker le bien dans le state local du composant
          setLocalBien(response)
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error)
      }
    }
    
    fetchBienData()
  }, [task, dispatch])
  
  // Initialiser le formulaire avec les données du bien local
  useEffect(() => {
    if (localBien) {
      formFields.forEach(field => {
        if (localBien[field.name] !== undefined) {
          setValue(field.name, localBien[field.name])
        }
      })
    }
  }, [localBien, formFields])

  // Watchers pour les toggles amiante et plomb (revalidation si toggle change)
  watch('diagnostic_plomb')
  watch('diagnostic_amiante')
  
  // Fonction de réinitialisation du formulaire
  const onReset = () => {
    reset()
    
    // Réinitialiser avec les valeurs du bien local
    if (localBien) {
      formFields.forEach(field => {
        if (localBien[field.name] !== undefined) {
          setValue(field.name, localBien[field.name])
        }
      })
    }
  }
  
  // Utiliser useFormConfirmation pour gérer les modifications non sauvegardées
  const {
    handleFormCancel,
    setInitialLoadComplete,
    setInitialValues,
    setFormIsDirty,
    FormConfirmDialog
  } = useFormConfirmation({
    watch,
    getValues,
    onReset,
    onClose
  })
  
  // Initialiser les valeurs initiales une fois les données chargées
  useEffect(() => {
    if (localBien) {
      setTimeout(() => {
        setInitialValues(getValues())
        setInitialLoadComplete(true)
        setFormIsDirty(false)
      }, 100)
    }
  }, [localBien])
  
  // Effet pour détecter les changements dans l'état de chargement
  useEffect(() => {
    if (isSubmitting && !isUpdating) {
      setIsSubmitting(false)
      if (!errors || Object.keys(errors).length === 0) {
        onSuccess()
      }
    }
  }, [isUpdating, errors, isSubmitting, onSuccess])
  
  // Fonction de soumission du formulaire
  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true)
      
      if (!localBien || !task.renewable || task.renewable.type !== "Bien") {
        throw new Error('Données manquantes')
      }
      
      // Mettre à jour le bien via le BienSlice
      await dispatch(updateBien({
        id: task.renewable.id,
        bienData: formData
      })).unwrap()
      
      // Marquer la tâche comme complétée seulement après la mise à jour réussie du bien
      await dispatch(completeRenewTask(task.id)).unwrap()
      
      // Appeler onSuccess seulement après que tout soit terminé
      onSuccess()
      
    } catch (error) {
      console.error('Erreur lors de la soumission:', error)
      setIsSubmitting(false)
    }
  }
  
  // Fonction pour récupérer le message d'erreur d'un champ
  const getErrorMessage = (fieldName) => {
    const formErrorMsg = formErrors?.[fieldName]?.message || ''
    const apiErrorMsg = errors?.[fieldName] || ''
    
    if (Array.isArray(apiErrorMsg)) {
      return apiErrorMsg.join(', ')
    }
    
    return formErrorMsg || apiErrorMsg
  }
  
  // Fonction pour vérifier si un champ a une erreur
  const hasError = (fieldName) => {
    return !!formErrors?.[fieldName] || !!errors?.[fieldName]
  }

  // Fonction pour générer les champs de formulaire incluant les fichiers pour les diagnostics
  const generateFormFields = (formFields, control) => formFields.map(
    (field, index) => getFormField(control, field, index)
  )
  
  // Fonction pour générer les champs de diagnostic spécifiques
  const generateDiagnosticFields = (taskType, control, bien) => {
    const diagnosticConfig = {
      diagnostic_dpe: {
        dateField: 'diagnostic_dpe_date',
        dateLabel: 'Date du DPE',
        echeanceField: 'diagnostic_dpe_date_echeance',
        echeanceLabel: "Date d'échéance",
        fileField: 'diagnostic_dpe_file',
        fileLabel: 'Fichier DPE',
        noteField: 'diagnostic_dpe_note',
        noteLabel: 'Note DPE'
      },
      diagnostic_gaz: {
        dateField: 'diagnostic_gaz_date',
        dateLabel: 'Date du diagnostic gaz',
        echeanceField: 'diagnostic_gaz_date_echeance',
        echeanceLabel: "Date d'échéance",
        fileField: 'diagnostic_gaz_file',
        fileLabel: 'Fichier diagnostic gaz'
      },
      diagnostic_electricite: {
        dateField: 'diagnostic_electricite_date',
        dateLabel: 'Date du diagnostic électricité',
        echeanceField: 'diagnostic_electricite_date_echeance',
        echeanceLabel: "Date d'échéance",
        fileField: 'diagnostic_electricite_file',
        fileLabel: 'Fichier diagnostic électricité'
      },
      diagnostic_plomb: {
        toggleField: 'diagnostic_plomb',
        dateField: 'diagnostic_plomb_date',
        dateLabel: 'Date du diagnostic plomb',
        echeanceField: 'diagnostic_plomb_date_echeance',
        echeanceLabel: "Date d'échéance",
        fileField: 'diagnostic_plomb_file',
        fileLabel: 'Fichier diagnostic plomb'
      },
      diagnostic_amiante: {
        toggleField: 'diagnostic_amiante',
        dateField: 'diagnostic_amiante_date',
        dateLabel: 'Date du diagnostic amiante',
        echeanceField: 'diagnostic_amiante_date_echeance',
        echeanceLabel: "Date d'échéance",
        fileField: 'diagnostic_amiante_file',
        fileLabel: 'Fichier diagnostic amiante'
      }
    }
    
    const config = diagnosticConfig[taskType]
    if (!config) return null
    
    return (
      <>
        {config.noteField && (
          <Input.Text
            control={control}
            name={config.noteField}
            label={config.noteLabel}
          />
        )}
        {config.toggleField && (
          <Toggle.Form
            control={control}
            name={config.toggleField}
            label={`${config.toggleField === 'diagnostic_plomb' ? 'Plomb' : 'Amiante'} détecté`}
          />
        )}
        <div className="flex flex-col items-start gap-4 col-span-2"> 
          <div className="flex flex-col gap-2">
            <Input.Date
              control={control}
              name={config.dateField}
              label={config.dateLabel}
              className="w-full"
            />
            {config.echeanceField && (
              <Input.Date
                control={control}
                name={config.echeanceField}
                label={config.echeanceLabel}
                className="w-full"
                rules={{
                  validate: (value) => {
                    const dateValue = getValues(config.dateField)
                    // Si un toggleField est spécifié, vérifier s'il est true (positif)
                    if(!value) {
                      if (config.toggleField) {
                        const toggleValue = getValues(config.toggleField)
                        // Si le toggle est false (négatif), pas d'obligation
                        if (!toggleValue) {
                          return true
                        }
                        return "Date d'échéance obligatoire si le diagnostic est positif"
                      }

                      if(!dateValue) {
                        return true
                      }
                      return "Date d'échéance obligatoire si la date du diagnostic est saisie"
                    }
                    return true
                  }
                }}
                validator={{ hasError, getErrorMessage }}
              />
            )}
          </div>
          <Input.File
            control={control}
            name={config.fileField}
            label={config.fileLabel}
            defaultValue={bien?.[config.fileField]?.filename || ''}
            inputProps={{ accept: '.pdf' }}
            canDelete={true}
          />
        </div>
      </>
    )
  }

  const generateAssuranceFields = (taskType, control, bien) => {
    // Extraire le type d'assurance (proprietaire ou locataire)
    // taskType peut être: assurance_proprietaire, assurance_locataire, assurance_proprietaire_verification_initiale, etc.
    const assuranceType = taskType.includes('locataire') ? 'locataire' : 'proprietaire'
    
    const toggleFieldName = `assurance_${assuranceType}`
    const releveFieldName = `assurance_${assuranceType}_date_dernier_releve`
    const echeanceFieldName = `assurance_${assuranceType}_date_echeance`
    
    return (
      <>
        {/* Toggle pour activer/désactiver l'assurance */}
        <Toggle.Form 
          control={control} 
          name={toggleFieldName}
          label={`Assurance ${assuranceType}`}
        />
        
        {/* Conteneur flex pour les deux dates */}
        <div className="flex flex-col items-start gap-4">
          {/* Date du dernier relevé */}
          <Input.Date
            control={control}
            name={releveFieldName}
            label={`Date du dernier relevé d'assurance ${assuranceType}`}
            className='w-full'
          />
          
          {/* Date d'échéance avec validation */}
          <Input.Date
            control={control}
            name={echeanceFieldName}
            label={`Date d'échéance assurance ${assuranceType}`}
            className='w-full'
            rules={{
              validate: (value) => {
                const releveValue = getValues(releveFieldName)
                if (releveValue && !value) {
                  return `Date d'échéance obligatoire si une date de dernier relevé est saisie`
                }
                return true
              }
            }}
            validator={{
              hasError: (fieldName) => !!formErrors?.[fieldName],
              getErrorMessage: (fieldName) => formErrors?.[fieldName]?.message || ''
            }}
          />
        </div>
      </>
    )
  }
  
  // Fonction pour gérer la soumission réussie du formulaire d'entretien
  const handleEntretienSuccess = async () => {
    try {
      // Marquer la tâche comme complétée
      await dispatch(completeRenewTask(task.id))
      onSuccess()
    } catch (error) {
      console.error('Erreur lors de la complétion de la tâche:', error)
    }
  }
  
  const getTitle = () => (
    <div className="flex justify-between items-center mb-4">
      <h2 className="text-xl font-bold mb-4">
        {task.title} - {task.description}
      </h2>
    </div>
  ) 

  // Déterminer si la tâche est une assurance ou un diagnostic
  const isInsuranceOrDiagnostic = task.task_type.startsWith('assurance_') || task.task_type.startsWith('diagnostic_')

  // Rendu conditionnel en fonction du type de tâche
  if (task.task_type === 'entretien' && localBien) {
    return (
      <div className="p-2">
        { getTitle() }
        
        {/* Utiliser le BienContext.Provider pour fournir le bien local au formulaire d'entretien */}
        <BienContext.Provider value={{ bien: localBien }}>
          <EntretienForm
            entretien_type_id={task.entretien_type_id}
            onClose={onClose}
            onSuccess={handleEntretienSuccess}
            useLocalState={true}
          />
        </BienContext.Provider>
      </div>
    )
  }
 
  return (
    <div className="p-2">
      { getTitle() }
      
      {/* Note d'information pour les assurances et diagnostics */}
      {isInsuranceOrDiagnostic && (
        <div className="alert mb-4 bg-primary-300 border-primary-300 text-primary-800">
          <InformationCircleIcon className="h-4 w-4"/>
          <div>
            <div className="text-sm space-y-2">
              <p>Pour confirmer cette tâche sans en créer une nouvelle : Enregistrer le formulaire sans réaliser de modifications.</p>
            </div>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="card bg-base-100 border border-base-200 rounded-none mb-4 max-h-[calc(80vh-150px)] overflow-y-auto">
          <div className="p-2 min-h-[380px]">
            <div className="card-body p-2">
              <div className="flex justify-between gap-4">
                <div className="flex gap-4 items-start align-center justify-around w-full">
                { (task.task_type.startsWith('diagnostic_') && localBien) && generateDiagnosticFields(task.task_type, control, localBien) }
                { (task.task_type.startsWith('assurance_') && localBien) && generateAssuranceFields(task.task_type, control, localBien) }
                { !isInsuranceOrDiagnostic && <>
                  {
                    formFields.length > 0 ? (
                      generateFormFields(formFields, control, localBien || {})
                    ) : (
                      <p className="text-gray-500">Aucun champ à afficher pour ce type de tâche.</p>
                    )
                  }
                </> }
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex justify-between">
          <button
            type="button"
            className="btn btn-outline"
            onClick={handleFormCancel}
          >
            Annuler
          </button>
          <button
            type="submit"
            className={`btn ${isSubmitting ? 'loading' : ''} btn-primary text-white`}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Enregistrement...' : 'Confirmer et terminer la tâche'}
          </button>
        </div>

      </form>
      
      {/* Dialogue de confirmation pour les modifications non sauvegardées */}
      <FormConfirmDialog />
    </div>
  )
}

export default RenewTaskForm